import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// all business sector users
export const fetchBusinessSectorUsers = createAsyncThunk(
  "business_sector_users/fetchAll",
  async (arg, { rejectWithValue }) => {
    const params =
      arg && typeof arg === "object" && !Array.isArray(arg)
        ? arg
        : { page: Number(arg) || 1 };
    try {
      const response = await axios.get(`${BASE_URL}/business-sector-users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
        params,
      });
      return response.data; // Return the whole response including pagination
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

// Single business sector user (GET /business-sector-users/:id)
export const fetchBusinessSectorUserDetail = createAsyncThunk(
  "business_sector_users/fetchDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/business-sector-users/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to load user details";
      return rejectWithValue(message);
    }
  }
);

// Add business sector user
export const addBusinessSectorUser = createAsyncThunk(
  "business_sector_users/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Append all form fields
      if (formData.business_sector_id) data.append("business_sector_id", formData.business_sector_id);
      if (formData.name) data.append("name", formData.name);
      data.append("name_en", formData.name_en ?? "");
      if (formData.phone) data.append("phone", formData.phone);
      if (formData.email) data.append("email", formData.email);
      if (formData.image) {
        data.append("image", formData.image);
      }

      const response = await axios.post(`${BASE_URL}/business-sector-users`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add user";
      return rejectWithValue(message);
    }
  }
);

// Update business sector user
export const updateBusinessSectorUser = createAsyncThunk(
  "business_sector_users/update",
  async (formData, { rejectWithValue }) => {
    try {
      const data = new FormData();
      if (formData.business_sector_user_id)
        data.append("business_sector_user_id", formData.business_sector_user_id);
      if (formData.business_sector_id)
        data.append("business_sector_id", formData.business_sector_id);
      if (formData.name) data.append("name", formData.name);
      data.append("name_en", formData.name_en ?? "");
      if (formData.phone) data.append("phone", formData.phone);
      if (formData.email) data.append("email", formData.email);
      if (formData.status !== undefined && formData.status !== null && formData.status !== "")
        data.append("status", String(formData.status));
      if (formData.password) data.append("password", formData.password);
      if (formData.is_mobile_verified !== undefined && formData.is_mobile_verified !== null && formData.is_mobile_verified !== "")
        data.append("is_mobile_verified", String(formData.is_mobile_verified));
      if (formData.is_email_verified !== undefined && formData.is_email_verified !== null && formData.is_email_verified !== "")
        data.append("is_email_verified", String(formData.is_email_verified));
      if (
        formData.has_fixed_code_verification !== undefined &&
        formData.has_fixed_code_verification !== null &&
        formData.has_fixed_code_verification !== ""
      )
        data.append("has_fixed_code_verification", String(formData.has_fixed_code_verification));
      if (formData.image) data.append("image", formData.image);

      const response = await axios.post(`${BASE_URL}/business-sector-users/update`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update user";
      return rejectWithValue(message);
    }
  }
);

const BusinessSectorUsersSlice = createSlice({
  name: "business_sector_users",
  initialState: {
    business_sector_users: [],
    isLoading: false,
    error: null,
    success: null,
    pagination: null,
    detail: null,
    detailLoading: false,
    detailError: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
    clearBusinessSectorUserDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessSectorUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinessSectorUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.business_sector_users = action.payload?.data?.data || [];
        state.pagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchBusinessSectorUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      // Add user
      .addCase(addBusinessSectorUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addBusinessSectorUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "User added successfully";
      })
      .addCase(addBusinessSectorUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add user";
      })
      // Update user
      .addCase(updateBusinessSectorUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateBusinessSectorUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "User updated successfully";
      })
      .addCase(updateBusinessSectorUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to update user";
      })
      .addCase(fetchBusinessSectorUserDetail.pending, (state) => {
        state.detailLoading = true;
        state.detail = null;
        state.detailError = null;
      })
      .addCase(fetchBusinessSectorUserDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload.data;
      })
      .addCase(fetchBusinessSectorUserDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detail = null;
        state.detailError = action.payload || "Failed to load details";
      });
  },
});

export const { clearState, clearBusinessSectorUserDetail } =
  BusinessSectorUsersSlice.actions;
export default BusinessSectorUsersSlice.reducer;
